import { v4 as uuidv4 } from 'uuid';
import BridgeError from '../model/bridge-error';
import ResponseMessage from '../model/response-message';
import RequestMessage from '../model/request-message';
import Page from '../model/page';
import PageRequest from '../model/page-request';
import { DefaultPageErrors } from '../model/page-error';
import { BridgeConfig } from '../model/bridge-config';

interface MessageHandler {
  resolve: (data: Record<string, any>) => void;
  reject: (error: BridgeError) => void;
}

interface PageOutHandler {
  send: (page: number) => void;
}

export default abstract class BaseBridge {
  config: BridgeConfig;

  messages: Record<string, MessageHandler> = {};

  outPages: Record<string, PageOutHandler> = {};

  inPages: Record<string, Page[]> = {};

  requestHandlers: Record<string, (request: RequestMessage) => Promise<Record<string, any>>> = {};

  constructor(config: BridgeConfig) {
    this.config = config;
  }

  onReceiveMessage = (event: MessageEvent): void => {
    if (event.origin !== this.config.origin) return;

    const data = event.data as Record<string, any> | undefined;

    if (data === undefined) return;

    // Request for next page of data
    if (PageRequest.isPageRequest(data)) {
      const request = PageRequest.from(data);
      if (!request) return;

      if (!this.outPages[request.id]) {
        this.sendJsonMessage(
          DefaultPageErrors.UNKNOWN_PAGE_ID(request.id, this.config.meta || {}).toJson(),
        );
        return;
      }
      if (request.complete) {
        delete this.outPages[request.id];
      } else {
        this.outPages[request.id].send(request.request);
      }
      return;
    }
    if (Page.isPage(data)) {
      const page = Page.from(data);
      if (page) {
        if (!this.inPages[page.id]) {
          this.inPages[page.id] = [];
        }
        this.inPages[page.id].push(page);
        if (this.inPages[page.id].length < page.pages) {
          this.sendJsonMessage(
            new PageRequest(page.id, this.config.meta || {}, page.current + 1, false).toJson(),
          );
        } else {
          this.sendJsonMessage(new PageRequest(page.id, this.config.meta || {}, -1, true).toJson());
          const payload = Page.createJsonFromPages(this.inPages[page.id]);
          delete this.inPages[page.id];
          this.processMessage(payload);
        }
      }
    }
  };

  sendResponse(originalRequest: RequestMessage, payload: Record<string, any>): void {
    const message = new ResponseMessage(
      originalRequest.id,
      originalRequest.name,
      this.config.meta || {},
      payload,
    );
    this.sendPages(message.toJson());
  }

  sendRequest(name: string, payload: Record<string, any>): Promise<Record<string, any>> {
    const id = uuidv4();

    const message = new RequestMessage(id, name, this.config.meta || {}, payload);

    return new Promise((resolve, reject) => {
      this.messages[id] = { resolve, reject };
      this.sendPages(message.toJson());
    });
  }

  async processMessage(data: Record<string, any>) {
    if (ResponseMessage.isResponse(data)) {
      const message = ResponseMessage.from(data);
      if (message) {
        if (this.messages[message.id] != null) {
          const handler = this.messages[message.id];

          delete this.messages[message.id];
          if (message.payload) {
            const { payload } = message;
            if (BridgeError.isError(payload)) {
              handler.reject(BridgeError.from(payload)!);
            } else {
              handler.resolve(payload);
            }
          } else {
            handler.resolve({});
          }
        }
      }
    } else if (RequestMessage.isRequest(data)) {
      const message = RequestMessage.from(data);
      if (message) {
        await this.requestHandlers[message.name]?.(message).then((payload: Record<string, any>) =>
          this.sendResponse(message, payload),
        );
      }
    }
  }

  abstract sendJsonMessage(message: Record<string, any>): void;

  private sendPages(message: Record<string, any>) {
    const id = uuidv4();
    const pages = Page.createPages(id, this.config.meta || {}, message);
    if (pages.length > 0) {
      this.outPages[id] = {
        send: (index: number) => {
          if (index >= 0 && index < pages.length) {
            const page = pages[index];
            this.sendJsonMessage(page.toJson());
            return true;
          }
          return false;
        },
      };

      this.sendJsonMessage(pages[0].toJson());
    }
  }

  addRequestHandler(
    name: string,
    callback: (request: RequestMessage) => Promise<Record<string, any>>,
  ): void {
    this.requestHandlers[name] = callback;
  }

  removeRequestHandler(name: string): void {
    delete this.requestHandlers[name];
  }
}
